import type { ErrorComponentProps } from '@tanstack/react-router'
import {
    AlertTriangle,
    Check,
    ChevronDown,
    Copy,
    FileCode,
    RefreshCcw,
    RotateCcw,
} from 'lucide-react'

import useTranslationStore from '@/language/useTranslationStore.ts'
import useRouteErrorLogic from '@/shared/RouteErrorComponent/Hooks/useRouteErrorLogic.ts'

export function RouteErrorComponent(props: ErrorComponentProps) {
    const { t } = useTranslationStore()
    const { handler, setter, state } = useRouteErrorLogic(props)
    const { details } = state

    return (
        <div className="bg-background-dark/80 animate-in fade-in fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 backdrop-blur-sm duration-500 sm:p-6">
            <div className="bg-surface-dark my-auto w-full max-w-5xl overflow-hidden rounded-2xl border border-red-500/20 shadow-2xl">
                <div className="flex items-start gap-3 border-b border-red-500/20 bg-red-500/10 px-5 py-4 sm:px-6">
                    <div className="rounded-lg bg-red-500/20 p-2 text-red-500">
                        <AlertTriangle size={24} />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight text-white">
                            {t('shared.routeError.title')}
                        </h1>
                        <p className="mt-1 text-sm leading-relaxed text-red-300/80">
                            {t(
                                `shared.routeError.descriptions.${details.category}`,
                            )}
                        </p>
                    </div>
                </div>

                <div className="space-y-5 p-5 sm:p-6">
                    <section className="border-border-dark bg-input-dark rounded-xl border p-4">
                        <div className="flex flex-wrap items-center gap-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
                            <span>{details.errorName}</span>
                            {details.statusCode && (
                                <span className="rounded-md bg-red-500/10 px-2 py-1 text-red-300">
                                    HTTP {details.statusCode}
                                </span>
                            )}
                        </div>
                        <p className="mt-3 font-mono text-sm leading-relaxed break-words text-red-300">
                            {details.message}
                        </p>
                        {details.cause && (
                            <p className="border-border-dark mt-3 border-t pt-3 text-sm text-gray-400">
                                <span className="font-semibold text-gray-300">
                                    {t('shared.routeError.cause')}:{' '}
                                </span>
                                {details.cause}
                            </p>
                        )}
                    </section>

                    <section className="border-border-dark overflow-hidden rounded-xl border">
                        <button
                            type="button"
                            onClick={() =>
                                setter.setIsTechnicalDetailsOpen(
                                    !state.isTechnicalDetailsOpen,
                                )
                            }
                            aria-expanded={state.isTechnicalDetailsOpen}
                            className="bg-input-dark hover:bg-background-dark/60 flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-3 text-left text-sm font-semibold text-gray-300 transition-colors"
                        >
                            <span className="flex items-center gap-2">
                                <FileCode size={16} />
                                {t('shared.routeError.technicalDetails')}
                            </span>
                            <ChevronDown
                                size={18}
                                className={`transition-transform ${state.isTechnicalDetailsOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {state.isTechnicalDetailsOpen && (
                            <div className="border-border-dark bg-background-dark/50 space-y-3 border-t p-4">
                                {details.errorPath && (
                                    <div>
                                        <p className="mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                                            {t('shared.routeError.filePath')}
                                        </p>
                                        <p className="font-mono text-xs break-all text-gray-300">
                                            {details.errorPath}
                                        </p>
                                    </div>
                                )}
                                <pre className="border-border-dark bg-input-dark custom-scrollbar max-h-80 overflow-auto rounded-lg border p-4 font-mono text-xs break-words whitespace-pre-wrap text-gray-500">
                                    {details.technicalDetails}
                                </pre>
                                <button
                                    type="button"
                                    onClick={handler.handleCopyTechnicalDetails}
                                    className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-400 transition-colors hover:text-white"
                                >
                                    {state.copied ? (
                                        <Check size={16} />
                                    ) : (
                                        <Copy size={16} />
                                    )}
                                    {t(
                                        state.copied
                                            ? 'shared.routeError.copied'
                                            : 'shared.routeError.copyDetails',
                                    )}
                                </button>
                            </div>
                        )}
                    </section>

                    <div className="border-border-dark flex flex-col gap-3 border-t pt-5 sm:flex-row">
                        <button
                            type="button"
                            onClick={handler.handleRetry}
                            className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-red-500 px-6 py-3 font-bold text-white transition-all hover:bg-red-400 active:scale-95 sm:w-auto"
                        >
                            <RotateCcw size={18} />
                            {t('shared.routeError.retry')}
                        </button>
                        <button
                            type="button"
                            onClick={handler.handleReload}
                            className="border-border-dark bg-input-dark inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border px-6 py-3 font-bold text-gray-300 transition-all hover:border-gray-600 hover:text-white active:scale-95 sm:w-auto"
                        >
                            <RefreshCcw size={18} />
                            {t('shared.routeError.reload')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
